/**
 * CatalogSlice — Public taxonomy data from /api/v2/catalog
 *
 * Provides all read-only taxonomy lookups for the User UI:
 *   - Categories, SubCategories
 *   - Workspace Classes, Professional Types
 *   - Service Groups, Master Services
 *   - Full hierarchy tree
 *
 * No authentication required. Data is cached with a 10-minute TTL.
 */

import { createSlice } from "@reduxjs/toolkit";
import { plainAxios } from "../../../../Common/Interceptors/AxiosConfig";

const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

const isFresh = (fetchedAt) => fetchedAt && Date.now() - fetchedAt < CACHE_TTL;

const initialState = {
  // ── Taxonomy lists ────────────────────────────────────────────
  categories: [],
  subcategories: [],
  workspaceClasses: [],
  professionalTypes: [],
  serviceGroups: [],
  services: [],

  // ── Full nested tree ──────────────────────────────────────────
  hierarchy: [],           // Category → SubCategory → WorkspaceClass → [ProfessionalType | ServiceGroup] → Service

  // ── Loading / error per key ───────────────────────────────────
  loading: {},             // { categories: true, hierarchy: false, ... }
  errors: {},             // { categories: "message", ... }

  // ── Cache timestamps ──────────────────────────────────────────
  fetchedAt: {},           // { categories: timestamp, ... }
};

const CatalogSlice = createSlice({
  name: "catalog",
  initialState,
  reducers: {
    setList: (state, { payload: { key, data } }) => {
      state[key] = data;
      state.fetchedAt[key] = Date.now();
      state.loading[key] = false;
      delete state.errors[key];
    },
    setLoading: (state, { payload: { key, value } }) => {
      state.loading[key] = value;
    },
    setError: (state, { payload: { key, message } }) => {
      state.errors[key] = message;
      state.loading[key] = false;
    },
    clearErrors: (state) => {
      state.errors = {};
    },
  },
});

const { setList, setLoading, setError } = CatalogSlice.actions;

// ── Generic fetch helper ─────────────────────────────────────────────────────
const fetchCatalog = (key, endpoint) => (params = {}) => async (dispatch, getState) => {
  const state = getState().catalog;

  // Serve from cache if fresh and no filters applied
  const hasParams = Object.keys(params).length > 0;
  if (!hasParams && state[key]?.length > 0 && isFresh(state.fetchedAt[key])) return;
  if (state.loading[key]) return;

  dispatch(setLoading({ key, value: true }));
  try {
    const response = await plainAxios.get(endpoint, { params });
    const { success, data } = response?.data || {};
    if (success) dispatch(setList({ key, data }));
    else dispatch(setError({ key, message: "Failed to fetch" }));
  } catch (err) {
    dispatch(setError({ key, message: err?.response?.data?.message || err.message || "Something went wrong" }));
  }
};

// ── Exported thunks ──────────────────────────────────────────────────────────

/** GET /api/v2/catalog/categories */
export const fetchCategories = fetchCatalog("categories", "catalog/categories");

/** GET /api/v2/catalog/subcategories?categoryId= */
export const fetchSubCategories = (categoryId) =>
  fetchCatalog("subcategories", "catalog/subcategories")(categoryId ? { categoryId } : {});

/** GET /api/v2/catalog/workspace-classes?subCategoryId= */
export const fetchWorkspaceClasses = (subCategoryId) =>
  fetchCatalog("workspaceClasses", "catalog/workspace-classes")(subCategoryId ? { subCategoryId } : {});

/** GET /api/v2/catalog/onboarding/workspace-classes?subCategoryId= */
export const fetchOnboardingClasses = (subCategoryId) =>
  fetchCatalog("workspaceClasses", "catalog/onboarding/workspace-classes")(subCategoryId ? { subCategoryId } : {});

/** GET /api/v2/catalog/professional-types?workspaceClassId= */
export const fetchProfessionalTypes = (workspaceClassId) =>
  fetchCatalog("professionalTypes", "catalog/professional-types")(workspaceClassId ? { workspaceClassId } : {});

/** GET /api/v2/catalog/service-groups?workspaceClassId= */
export const fetchServiceGroups = (workspaceClassId) =>
  fetchCatalog("serviceGroups", "catalog/service-groups")(workspaceClassId ? { workspaceClassId } : {});

/** GET /api/v2/catalog/services?serviceGroupId= */
export const fetchServices = (serviceGroupId) =>
  fetchCatalog("services", "catalog/services")(serviceGroupId ? { serviceGroupId } : {});

/**
 * GET /api/v2/catalog/hierarchy
 * Full nested tree — Category → SubCategory → WorkspaceClass → [ProfessionalType | ServiceGroup] → Service
 */
export const fetchCatalogHierarchy = () => async (dispatch, getState) => {
  const state = getState().catalog;
  if (state.hierarchy?.length > 0 && isFresh(state.fetchedAt["hierarchy"])) return;
  if (state.loading["hierarchy"]) return;

  dispatch(setLoading({ key: "hierarchy", value: true }));
  try {
    const response = await plainAxios.get("catalog/hierarchy");
    const { success, data } = response?.data || {};
    if (success) dispatch(setList({ key: "hierarchy", data }));
    else dispatch(setError({ key: "hierarchy", message: "Failed to load hierarchy" }));
  } catch (err) {
    dispatch(setError({ key: "hierarchy", message: err?.response?.data?.message || err.message || "Something went wrong" }));
  }
};

// ── Selectors ────────────────────────────────────────────────────────────────
export const selectCategories = (s) => s.catalog.categories;
export const selectSubCategories = (s) => s.catalog.subcategories;
export const selectWorkspaceClasses = (s) => s.catalog.workspaceClasses;
export const selectProfessionalTypes = (s) => s.catalog.professionalTypes;
export const selectServiceGroups = (s) => s.catalog.serviceGroups;
export const selectServices = (s) => s.catalog.services;
export const selectCatalogHierarchy = (s) => s.catalog.hierarchy;
export const selectCatalogLoading = (key) => (s) => s.catalog.loading[key] || false;
export const selectCatalogError = (key) => (s) => s.catalog.errors[key] || null;

export const { clearErrors } = CatalogSlice.actions;

export default CatalogSlice.reducer;
